import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Radar, Filter, ExternalLink, Loader2, AlertCircle, Calendar,
  Building2, MapPin, Users, CheckCircle2, RefreshCw, FileSearch,
} from 'lucide-react';
import CandidateTrendPanel from '../components/CandidateTrendPanel';

const API = process.env.REACT_APP_BACKEND_URL;

const CARGOS = [
  'PRESIDENTE',
  'GOVERNADOR',
  'SENADOR',
  'DEPUTADO FEDERAL',
  'DEPUTADO ESTADUAL',
  'PREFEITO',
  'VEREADOR',
];

const UFS = [
  'AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
  'MG', 'MS', 'MT', 'PA', 'PB', 'PE', 'PI', 'PR', 'RJ', 'RN',
  'RO', 'RR', 'RS', 'SC', 'SE', 'SP', 'TO',
];

const fmtDate = (iso) => {
  if (!iso) return 'n/d';
  try {
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
  } catch { return iso; }
};

const fmtDateTime = (iso) => {
  if (!iso) return 'n/d';
  try { return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return iso; }
};

const fmtInt = (n) => {
  if (n === null || n === undefined) return 'n/d';
  return new Intl.NumberFormat('pt-BR').format(n);
};

const RadarEleitoral = () => {
  const [meta, setMeta] = useState(null);
  const [pesquisas, setPesquisas] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('loading');
  const [mensagem, setMensagem] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading] = useState(false);

  // Filters
  const [year, setYear] = useState('all');
  const [uf, setUf] = useState('all');
  const [municipio, setMunicipio] = useState('');
  const [cargo, setCargo] = useState('all');
  const [instituto, setInstituto] = useState('');
  const [onlyRecent, setOnlyRecent] = useState(false);

  const years = useMemo(() => {
    const base = meta?.years || [];
    return base.length ? base : [2024, 2022, 2020, 2018];
  }, [meta]);

  const loadMeta = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/api/radar/meta`, { timeout: 10000 });
      setMeta(resp.data);
    } catch { /* silent */ }
  }, []);

  const loadPesquisas = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (year !== 'all') params.year = year;
      if (uf !== 'all') params.uf = uf;
      if (municipio.trim()) params.municipio = municipio.trim();
      if (cargo !== 'all') params.cargo = cargo;
      if (instituto.trim()) params.instituto = instituto.trim();
      if (onlyRecent) params.days = 30;

      const resp = await axios.get(`${API}/api/radar/pesquisas`, { params, timeout: 20000 });
      setPesquisas(resp.data.pesquisas || []);
      setTotal(resp.data.total || 0);
      setStatus(resp.data.status || 'ok');
      setMensagem(resp.data.mensagem || '');
      setLastUpdated(resp.data.last_updated);
    } catch {
      setStatus('erro');
      setMensagem('Não foi possível carregar as pesquisas. Tente novamente em instantes.');
    } finally {
      setLoading(false);
    }
  }, [year, uf, municipio, cargo, instituto, onlyRecent]);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { loadPesquisas(); }, [loadPesquisas]);

  const resetFilters = () => {
    setYear('all'); setUf('all'); setMunicipio(''); setCargo('all');
    setInstituto(''); setOnlyRecent(false);
  };

  return (
    <div className="space-y-5" data-testid="radar-page">
      {/* Header */}
      <div className="text-center py-4 md:py-6">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#E8F5E9] border border-[#006030]/20 rounded-full mb-3">
          <Radar className="w-3.5 h-3.5 text-[#006030]" />
          <span className="text-[11px] font-bold text-[#006030] uppercase tracking-wider">Fonte oficial: TSE Dados Abertos</span>
        </div>
        <h1 className="text-2xl sm:text-3xl lg:text-5xl font-black text-[#0F1C2E] tracking-tight mb-2" data-testid="radar-title">
          Radar Eleitoral
        </h1>
        <p className="text-sm sm:text-base text-[#475569] max-w-2xl mx-auto px-2">
          Acompanhe pesquisas eleitorais oficialmente registradas no TSE, organizadas por estado, município e cargo.
        </p>
        {lastUpdated && (
          <p className="text-[10px] text-[#94A3B8] mt-2" data-testid="radar-last-updated">
            Última atualização: {fmtDateTime(lastUpdated)}
          </p>
        )}
      </div>

      {/* Filters */}
      <Card className="p-4 bg-white border border-[#E2E8F0] rounded-md" data-testid="radar-filters">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-[#006030]" />
          <h3 className="text-sm font-bold text-[#0F1C2E]">Filtros</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <div>
            <Label className="text-[10px] font-bold text-[#94A3B8] uppercase">Ano</Label>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="mt-1 border-[#E2E8F0]" data-testid="filter-year"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] font-bold text-[#94A3B8] uppercase">Estado</Label>
            <Select value={uf} onValueChange={setUf}>
              <SelectTrigger className="mt-1 border-[#E2E8F0]" data-testid="filter-uf"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {UFS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] font-bold text-[#94A3B8] uppercase">Cargo</Label>
            <Select value={cargo} onValueChange={setCargo}>
              <SelectTrigger className="mt-1 border-[#E2E8F0]" data-testid="filter-cargo"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {CARGOS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] font-bold text-[#94A3B8] uppercase">Município</Label>
            <Input
              value={municipio}
              onChange={e => setMunicipio(e.target.value)}
              placeholder="Ex: São Paulo"
              className="mt-1 border-[#E2E8F0]"
              data-testid="filter-municipio"
            />
          </div>
          <div>
            <Label className="text-[10px] font-bold text-[#94A3B8] uppercase">Instituto</Label>
            <Input
              value={instituto}
              onChange={e => setInstituto(e.target.value)}
              placeholder="Ex: Datafolha"
              className="mt-1 border-[#E2E8F0]"
              data-testid="filter-instituto"
            />
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-xs text-[#475569] cursor-pointer">
              <input
                type="checkbox"
                checked={onlyRecent}
                onChange={e => setOnlyRecent(e.target.checked)}
                className="w-3.5 h-3.5"
                data-testid="filter-recent"
              />
              Apenas últimos 30 dias
            </label>
          </div>
        </div>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#E2E8F0]">
          <p className="text-[11px] text-[#94A3B8]">
            {loading ? 'Buscando…' : `${total} pesquisa${total === 1 ? '' : 's'} encontrada${total === 1 ? '' : 's'}`}
          </p>
          <Button variant="outline" size="sm" onClick={resetFilters} className="text-[11px] h-7" data-testid="filter-reset">
            Limpar filtros
          </Button>
        </div>
      </Card>

      {/* Candidate trends (sparklines) — renders silently when no data */}
      <CandidateTrendPanel
        year={year}
        uf={uf}
        cargo={cargo === 'all' ? 'PRESIDENTE' : cargo}
        municipio={municipio}
      />

      {/* Status / empty / loading */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-[#006030]" />
        </div>
      )}

      {!loading && pesquisas.length === 0 && status === 'indisponivel' && (
        <Card className="p-8 bg-[#FFF8E1] border border-[#F0D000]/40 rounded-md text-center" data-testid="radar-empty-tse">
          <RefreshCw className="w-10 h-10 text-[#F57F17] mx-auto mb-3" />
          <h3 className="text-lg font-bold text-[#0F1C2E] mb-1">Aguardando dados do TSE</h3>
          <p className="text-sm text-[#475569] max-w-xl mx-auto">{mensagem}</p>
          <p className="text-[11px] text-[#94A3B8] mt-4">
            O sistema verifica automaticamente o Portal de Dados Abertos do TSE todos os dias.
          </p>
        </Card>
      )}

      {!loading && pesquisas.length === 0 && status === 'erro' && (
        <Card className="p-8 bg-[#FFEBEE] border border-[#C62828]/30 rounded-md text-center" data-testid="radar-error">
          <AlertCircle className="w-10 h-10 text-[#C62828] mx-auto mb-3" />
          <p className="text-sm text-[#C62828]">{mensagem}</p>
        </Card>
      )}

      {!loading && pesquisas.length === 0 && status === 'ok' && (
        <Card className="p-8 bg-white border border-[#E2E8F0] rounded-md text-center" data-testid="radar-empty-filter">
          <FileSearch className="w-10 h-10 text-[#94A3B8] mx-auto mb-3" />
          <p className="text-sm text-[#475569]">Nenhuma pesquisa encontrada para os filtros selecionados.</p>
        </Card>
      )}

      {/* Results list */}
      {!loading && pesquisas.length > 0 && (
        <div className="space-y-3" data-testid="radar-results">
          {pesquisas.map((p) => (
            <Card
              key={`${p.ano}-${p.nr_registro}`}
              className={`p-4 bg-white border rounded-md ${p.novo ? 'border-[#006030] shadow-md' : 'border-[#E2E8F0]'}`}
              data-testid={`pesquisa-${p.nr_registro}`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    {p.novo && <Badge className="bg-[#006030] text-white text-[10px]">Nova</Badge>}
                    {p.ds_cargo && <Badge variant="outline" className="text-[10px] border-[#1E3A5F] text-[#1E3A5F]">{p.ds_cargo}</Badge>}
                    {p.sg_uf && <Badge variant="outline" className="text-[10px]">{p.sg_uf}</Badge>}
                    {p.nm_municipio && <span className="text-[11px] text-[#475569] flex items-center gap-1"><MapPin className="w-3 h-3" />{p.nm_municipio}</span>}
                  </div>
                  <h3 className="text-base font-bold text-[#0F1C2E] truncate">
                    {p.nm_instituto || 'Instituto não informado'}
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2 text-[11px] text-[#475569]">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3 h-3 text-[#94A3B8]" />
                      <span>Divulgada: <strong>{fmtDate(p.dt_divulgacao)}</strong></span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3 h-3 text-[#94A3B8]" />
                      <span>Coleta: {fmtDate(p.dt_inicio)} a {fmtDate(p.dt_fim)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Users className="w-3 h-3 text-[#94A3B8]" />
                      <span>{fmtInt(p.qt_entrevistados)} entrevistados</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3 text-[#006030]" />
                      <span>Margem de erro: {p.margem_erro != null ? `${p.margem_erro}%` : 'n/d'}</span>
                    </div>
                  </div>
                  {p.nm_contratante && (
                    <p className="text-[11px] text-[#94A3B8] mt-2 flex items-center gap-1">
                      <Building2 className="w-3 h-3" /> Contratante: {p.nm_contratante}
                    </p>
                  )}
                </div>

                <div className="flex flex-col items-end gap-2 shrink-0">
                  <Badge className="bg-[#F4F6F8] text-[#0F1C2E] text-[10px]">
                    Registro TSE: <strong className="ml-1">{p.nr_registro}</strong>
                  </Badge>
                  <div className="flex gap-2">
                    <a
                      href={p.tse_ref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[11px] font-bold text-[#006030] hover:underline"
                      data-testid={`tse-link-${p.nr_registro}`}
                    >
                      Ver no TSE <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <p className="text-center text-[10px] text-[#94A3B8] max-w-2xl mx-auto pt-2">
        Os dados exibidos são reproduzidos diretamente do Portal de Dados Abertos do TSE. O Tô de Olho não interpreta,
        pondera ou adiciona comentários aos resultados. A leitura política é responsabilidade exclusiva do eleitor.
      </p>

      <div className="text-center pt-2">
        <Link to="/" className="text-[11px] text-[#006030] hover:underline">Voltar à busca de candidatos</Link>
      </div>
    </div>
  );
};

export default RadarEleitoral;
